import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { parse } from 'query-string';

import { SimpleLoader, Paper } from '@dev/ee-components';
import { isNil } from 'lodash-es';
import dayjs from 'dayjs';
import { CampaignStatus, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import useMobile from '@/services/hooks/useMobile';
import { getCampaignsStore, GetCampaigns, UnpickAllCampaigns } from '@/store/campaigns/actions';
import { GetCampaignsPayload } from '@/store/campaigns/types';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { useReshardingActions } from '@/services/hooks/useReshardingActions';
import CamapginsListHeader from './components/CampaignsListHeader';
import CampaignsListResults from './components/CampaignsListResults';
import CampaignsListFooter from './components/CampaignsListFooter';

import { screenCampaignTypes } from './config';

const CampaignsList: FC = (): JSX.Element => {
    const campaigns = useSelector(getCampaignsStore);
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobile = useMobile();

    const { checkReshardStatusIfNeeded } = useReshardingActions();

    const query = parse(location.search);

    useEffect((): (() => void) => {
        checkReshardStatusIfNeeded();
        window.scrollTo(0, 0);

        const { page, perPage, search, to, from, status, type } = query;
        const offset = !perPage || !page ? null : Number(page) * Number(perPage) - Number(perPage);
        let queryParams: GetCampaignsPayload = {};

        queryParams = {
            ...(isNil(perPage) ? {} : { limit: Number(perPage) }),
            ...(isNil(offset) ? {} : { offset }),
            ...(isNil(search) ? {} : { search: search as string }),
            ...(isNil(to)
                ? {}
                : {
                      to: dayjs(to.toString()).format(LONG_DATE_FORMAT),
                  }),
            ...(isNil(from) ? {} : { from: dayjs(from.toString()).format(LONG_DATE_FORMAT) }),
            ...(isNil(type) ? {type: screenCampaignTypes} :
                        typeof status === 'string'
                        ? { type: [type as CampaignTypeFilter] }
                        : { type: type as CampaignTypeFilter[] }),
            ...(isNil(status)
                ? {}
                : typeof status === 'string'
                ? { status: [status as CampaignStatus] }
                : { status: status as CampaignStatus[] }),
        };
        dispatch(GetCampaigns({ ...queryParams }));

        return (): void => {
            dispatch(UnpickAllCampaigns());
        };
    }, []);

    if (!campaigns) {
        return <SimpleLoader />;
    }

    const components = (
        <>
            <CamapginsListHeader />
            <CampaignsListResults />
            {!campaigns?.loading && !!campaigns?.statusCount?.total && <CampaignsListFooter />}
        </>
    );

    return isMobile ? components : <Paper margin={{ top: 32 }}>{components}</Paper>;
};

export default CampaignsList;
